import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSettings, Toast, DataTable, DataTableColumn } from '@salesforce/design-system-react';
import { ITaskRun } from 'interfaces/IHistory.interface';
import { SFConnection } from 'common/sf-connector';
import { CQAppConstant } from 'app-constants';
import { capitalizeFirstLetter, getErrorTableKeys, parseErrorMessage, sanitizeErrorObjects } from 'common/error.utils';
import { getNavigationLabelAndUrl } from 'common/module.utils';

interface CQTaskErrorProps {
    failedTask?: ITaskRun;
    onClose: () => void;
}

/**
 * Component that displays error details for a failed task execution.
 * Shows parsed error messages in either a table format (for structured errors)
 * or as plain text, with a link to view deployment status in Salesforce.
 *
 * @param failedTask - The failed task object containing error details
 * @param onClose - Callback function to handle closing the error toast
 * @returns A toast component with error details and optional error table for structured errors
 */
export default function CQTaskError({ failedTask, onClose }: CQTaskErrorProps) {
    const { t, i18n } = useTranslation();
    // Support both legacy plain-string errMessage and new { taskType, detail } shape
    const errMessage = failedTask?.errMessage;
    const taskType = typeof errMessage === 'object' && errMessage !== null ? errMessage.taskType : undefined;
    const errorDetail = typeof errMessage === 'string' ? errMessage : errMessage?.detail;
    const parsedError = parseErrorMessage(errorDetail);
    const [migrationReportUrl, setMigrationReportUrl] = useState<string>('');

    useEffect(() => {
        if (taskType !== CQAppConstant.TASK_TYPES.MIGRATION_TASK_TYPE) {
            setMigrationReportUrl('');
            return;
        }
        let isMounted = true; // fetch is in progress, component is still mounted
        SFConnection.getMigrationLogsReportUrl()
            .then((url) => {
                if (isMounted) {
                    setMigrationReportUrl(url);
                }
            });
        return () => {
            isMounted = false;
        };
    }, [taskType, failedTask?.uniqueName]);

    const taskName = failedTask?.name || '';
    const taskKey = `task_${taskName}`;
    const stepName = failedTask?.label || (taskName && i18n.exists(taskKey) ? t(taskKey) : taskName);

    const { label: navigationLinkLabel, url: navigationUrl } = getNavigationLabelAndUrl(taskType, migrationReportUrl);

    /**
     * Opens the relevant status URL in a new tab.
     * @param e - React click event
     */
    const handleStatusClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        window.open(navigationUrl, '_blank', 'noopener,noreferrer');
    };

    const deploymentStatusLink = (
        <a href="#" onClick={handleStatusClick}>
            {t(navigationLinkLabel)}
        </a>
    );

    let heading: React.ReactNode;
    let errorTable: React.ReactNode = null;
    let errorHeadingText = taskType === CQAppConstant.TASK_TYPES.SF_DEPLOY_PACKAGES? t('external_package_deployment_failed'): t('task_failed_with_details', { stepName })

    if (Array.isArray(parsedError) && parsedError.length > 0) {
        const sanitizedErrors = sanitizeErrorObjects(parsedError);
        const errorColumnKeys = getErrorTableKeys(sanitizedErrors);
        const tableItems = sanitizedErrors.map((item, index) => ({ id: String(index), ...item }));
        heading = (
            <div className="slds-size_1-of-1" style={{ wordBreak: 'break-word' }}>
                <p className="">{errorHeadingText}</p>
                <p className="slds-m-bottom_none">{t('fix_issue_and_continue')} {deploymentStatusLink} {t('or_see_error_details')}</p>
            </div>
        );
        errorTable = (
            <div className="slds-text-longform slds-size_1-of-1" style={{ wordBreak: 'break-word' }}>
                <div className="slds-scrollable slds-border_left slds-border_right slds-border_top slds-border_bottom" style={{ maxHeight: '120px' }}>
                <DataTable
                    id="cq-error-table"
                    columnBordered
                    fixedLayout
                    items={tableItems}
                    className="slds-text-color_default"
                    noRowHover
                >
                    {errorColumnKeys.map(columnKey => (
                        <DataTableColumn key={columnKey} label={capitalizeFirstLetter(columnKey)} property={columnKey} />
                    ))}
                </DataTable>
            </div>
            </div>
        );
    } else if (typeof parsedError === CQAppConstant.DATA_TYPES.STRING && parsedError) {
        heading = (
            <div style={{ wordBreak: 'break-word' }}>
                <p>{errorHeadingText} {t('error_with_message', { errorString: parsedError })} {deploymentStatusLink}</p>
                <p>{t('fix_issue_and_continue')}</p>
            </div>
        );
    } else {
        heading = (
            <div style={{ wordBreak: 'break-word' }}>
                <p>{errorHeadingText} {t('task_failed_error_message')} {deploymentStatusLink}</p>
            </div>
        );
    }

    return (
        <IconSettings iconPath="/assets/icons">
            <div role="alert" className="slds-theme_error slds-p-left_xx-small slds-p-right_xx-small slds-p-bottom_xx-small slds-m-bottom_xx-small">
                <Toast
                    className="slds-size_1-of-1 slds-border_radius_none slds-m-around_none"
                    labels={{ heading }}
                    onRequestClose={onClose}
                    variant="error"
                    style={{ outline: 'none', boxShadow: 'none' }}
                />
               {errorTable &&  errorTable}         
            </div>
        </IconSettings>
    );
}
